import React, { useState, useEffect, useRef } from 'react';
import { atom, useRecoilState, useRecoilValue } from 'recoil';
import { useThemeUI, Box, ThemeProvider } from 'theme-ui';
import { _set } from '@monobits/core';
import { pull } from 'lodash';
import { useConditionalEffect, useUpdateEffect, useIntersectionObserver } from '@react-hookz/web';
import { gsap } from 'gsap';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}
/**
 * Simple Recoil JS Atom, Global Color Mode State
 */


var targetModeState = atom({
  key: 'targetModeState',
  default: undefined
});
/**
 * Always return a human readable mode name, fix for Gatsby SSR
 *
 * @param {string} mode colorMode from Theme UI
 * @param {string} initial InitialColorModeName from Theme UI
 */

var fixSSRInitialMode = function fixSSRInitialMode(mode, initial) {
  if (initial === void 0) {
    initial = 'light';
  }

  return mode === undefined ? undefined : initial;
};
/**
 * The color mode name that is not the initial one
 *
 * @param {string} initial InitialColorModeName from Theme UI
 * @param {string} pair Array of two color mode names
 */


var nextColorMode = function nextColorMode(initial, pair) {
  if (pair === void 0) {
    pair = ['light', 'dark'];
  }

  return pull(pair, initial).join();
};
/**
 * The "current" mode name
 *
 * @param {string} mode colorMode from Theme UI
 * @param {string} initial InitialColorModeName from Theme UI
 */


var currentMode = function currentMode(mode, initial) {
  if (initial === void 0) {
    initial = 'light';
  }

  return mode === fixSSRInitialMode(mode, initial) ? initial : mode;
};
/**
 * Toggle between light/dark mode
 *
 * @param {string} mode colorMode from Theme UI
 * @param {string} initial InitialColorModeName from Theme UI
 * @param {Array.<string>} pair Array of two color mode names
 */


var toggleModes = function toggleModes(mode, initial, pair) {
  return currentMode(mode, initial) === initial ? nextColorMode(initial, pair) : initial;
};
/**
 * The color mode name that is not the initial one
 *
 * @param {string} initial InitialColorModeName from Theme UI
 * @param {Array.<string>} pair Array of two color mode names
 */


var nextMode = function nextMode(initial, pair) {
  return nextColorMode(initial, pair);
};
/**
 * Custom Hook to handle Theme UI color modes
 *
 * @param {Object} props.context Theme UI Context
 *
 * @example
 * const { setTarget, toggle } = useModes()
 *
 * <button onclick={() => setTarget(toggle)} />
 */


var useModes = function useModes() {
  var _useThemeUI = useThemeUI(null),
      colorMode = _useThemeUI.colorMode,
      _useThemeUI$theme = _useThemeUI.theme,
      initialColorModeName = _useThemeUI$theme.config.initialColorModeName,
      modes = _useThemeUI$theme.rawColors.modes;

  var _useRecoilState = useRecoilState(targetModeState),
      target = _useRecoilState[0],
      setTarget = _useRecoilState[1];

  var _useState = useState(initialColorModeName),
      current = _useState[0],
      setCurrent = _useState[1];

  var options = {
    initial: initialColorModeName,
    next: nextMode(initialColorModeName),
    toggle: toggleModes(current, initialColorModeName)
  };
  useEffect(function () {
    if (current !== colorMode) setCurrent(currentMode(colorMode, initialColorModeName));
  }, [colorMode]); // eslint-disable-line

  useEffect(function () {
    return _set.attr('theme-mode', current);
  }, [current]);
  /**
   * Retured props
   *
   * @param {string} target The color mode to target for change
   * @param {function} setTarget Set the target color mode
   * @param {string} initial The initial colorMode
   * @param {string} next The "other" color mode (light or dark)
   * @param {string} toggle Toggle between (light or dark)
   */

  return _extends({
    target: target || current,
    setTarget: setTarget,
    modes: modes
  }, options);
};

var toVarName = function toVarName(key) {
  return "--theme-ui-" + key.replace('-__default', '');
};

var join = function join() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return args.filter(Boolean).join('-');
};
/**
 * Transform colors object into CSS Custom Properties. (Copied from internal Theme UI)
 *
 * @param {string} parent The name of the parent key. EX: 'colors, spaces, ...'
 * @param {Object} obj The color and modes object
 */


var objectToVars = function objectToVars(parent, obj) {
  var vars = {};

  for (var key in obj) {
    if (key === 'modes') continue;
    var name = join(parent, key);
    var value = obj[key];

    if (value && typeof value === 'object') {
      vars = _extends({}, vars, objectToVars(name, value));
    } else {
      vars[toVarName(name)] = value;
    }
  }

  return vars;
};
/**
 * @internal
 * ClassName applied when the tween is active.
 *
 * @type {string}
 */


var ACTIVE_CLASS = '-in-transition';
/**
 * Component that interpolates/tweens between Theme UI colors modes changes
 *
 * @param {function} props.extend Function that allow to extend the timeline
 *
 * @param {function} extend.tl (exposed) The timeline instance
 * @param {string} extend.target (exposed) The target color mode
 * @param {Object} extend.options (exposed) The timeline transition options
 *
 * @example
 * <RecoilRoot>
 *   <ColorModeTransition />
 *   {children}
 * </RecoilRoot>
 *
 * @example
 * <RecoilRoot>
 *   <ColorModeTransition extend={({ tl, target, options }) => {
 *     return tl.to(target, { rotation: 180, ...options }, 'start')
 *   }} />
 *   {children}
 * </RecoilRoot>
 */

var ColorModeTransition = function ColorModeTransition(_ref) {
  var _ref$extend = _ref.extend,
      extend = _ref$extend === void 0 ? function () {} : _ref$extend,
      custom = _ref.custom;
  var target = useRecoilValue(targetModeState);

  var _useThemeUI = useThemeUI(null),
      setColorMode = _useThemeUI.setColorMode,
      _useThemeUI$theme = _useThemeUI.theme,
      modes = _useThemeUI$theme.rawColors.modes,
      options = _useThemeUI$theme.config.colorTransitions;

  var timeline = useRef(null);
  useConditionalEffect(function () {
    var ref = custom ? !!(custom != null && custom.ref) ? custom == null ? void 0 : custom.ref : null : document.documentElement;
    var type = custom != null && custom.ref ? 'nested' : 'root';
    var sources = {
      root: modes,
      nested: custom == null ? void 0 : custom.modes
    }[type];
    var vars = objectToVars('colors', sources == null ? void 0 : sources[target]);
    var entries = Object.entries(vars);

    var handleSwitch = function handleSwitch(target) {
      return !(custom != null && custom.ref) ? setColorMode(target) : {};
    };

    var properties = entries.reduce(function (acc, _ref2) {
      var _extends2;

      var key = _ref2[0],
          value = _ref2[1];
      return _extends({}, acc, (_extends2 = {}, _extends2[key] = value, _extends2));
    }, {});
    timeline.current = gsap.timeline({
      paused: true,
      onUpdate: function onUpdate() {
        return _set.class(ACTIVE_CLASS);
      },
      onComplete: function onComplete() {
        return _set.class(ACTIVE_CLASS, false);
      }
    });
    var clearProps = [].concat(Object.keys(properties), ['will-change']).join();
    timeline.current.addLabel('start').to(ref, _extends({}, properties, options), 'start').call(handleSwitch, [target], 'start+=0.05').addLabel('end').set(ref, {
      willChange: 'auto'
    }, 'start').set(ref, {
      clearProps: clearProps
    }, 'end');
    if ((extend == null ? void 0 : extend.constructor) === Function) extend({
      tl: timeline.current,
      target: target,
      options: options
    });
  }, [target], [target, modes, custom ? !!(custom != null && custom.ref) : true, custom ? !!(custom != null && custom.visible) : true], undefined, useUpdateEffect);
  useEffect(function () {
    if (timeline.current != null) timeline.current.play();
  }, [target]);
  return null;
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _excluded = ["children", "modes", "observe", "shouldTween", "ssx", "sx"];
/**
 * Nested theme color mode,
 * Required by `ColorModeTransition` for transitions to work on nested providers.
 *
 * @param {function} props.modes The new modes for the nested provider
 * @property {Object} modes.colors Exposes the main theme `rawColors` object
 *
 * @param {boolean} props.observe Wether to tween the change if in / out of the viewport
 * @param {boolean} props.shouldTween Override the observer and manage tween behavior manually
 *
 * @param {boolean} props.ssx Common styles to the parent & provider.
 *
 * @example
 * const InvertedMode = (props) => <NestedMode modes={(colors) => ({ ...colors })} {...props} />
 */

var NestedMode = function NestedMode(_ref) {
  var _useThemeUI$theme, _theme$colors, _ref$current, _ref$current$childNod;

  var children = _ref.children,
      modes = _ref.modes,
      _ref$observe = _ref.observe,
      observe = _ref$observe === void 0 ? true : _ref$observe,
      _ref$shouldTween = _ref.shouldTween,
      shouldTween = _ref$shouldTween === void 0 ? null : _ref$shouldTween,
      ssx = _ref.ssx,
      sx = _ref.sx,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  var _ref2 = (_useThemeUI$theme = useThemeUI().theme) != null ? _useThemeUI$theme : {},
      rawColors = _ref2.rawColors;

  var theme = modes(rawColors);
  var ref = useRef(null);
  var intersection = useIntersectionObserver(ref, {
    rootMargin: '100px'
  });
  var visible = observe ? (intersection == null ? void 0 : intersection.intersectionRatio) > 0 : true;
  return /*#__PURE__*/React.createElement(Box, _extends({
    ref: ref,
    sx: _extends({
      '>[data-themeui-nested-provider]': ssx
    }, ssx, sx)
  }, props), /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(ColorModeTransition, {
    custom: {
      modes: (_theme$colors = theme.colors) == null ? void 0 : _theme$colors.modes,
      ref: (_ref$current = ref.current) == null ? void 0 : (_ref$current$childNod = _ref$current.childNodes) == null ? void 0 : _ref$current$childNod[0],
      visible: shouldTween != null ? shouldTween : visible
    }
  }), children));
};

export { ColorModeTransition, NestedMode, targetModeState, useModes };